import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronDown } from "lucide-react";
import Reveal from "@/components/Reveal";
import { useLang } from "@/i18n";
import { useContent } from "@/content/ContentContext";

const FormulaCard = ({ formula, index }) => {
  const { lang, t } = useLang();
  const f = t.formule;
  const copy = f.items[formula.id];
  const [open, setOpen] = useState(false);

  return (
    <article
      className="group flex flex-col bg-ivory border border-sand/80"
      data-testid={`formula-card-${formula.id}`}
      data-edit-formula={formula.id}
    >
      <div className="frame">
        <img
          src={formula.image}
          alt={copy.name}
          loading="lazy"
          className="w-full aspect-[16/10] object-cover"
        />
      </div>
      <div className="p-7 md:p-10 flex flex-col flex-1">
        <div className="flex items-baseline gap-4">
          <span className="font-serif italic text-xl text-sand">{String(index + 1).padStart(2, "0")}</span>
          <h3 className="font-serif text-3xl md:text-4xl tracking-tight">{copy.name}</h3>
        </div>
        <p className="mt-4 text-[11px] uppercase tracking-[0.25em] text-olive">{copy.kicker}</p>
        <p className="mt-5 text-base font-light leading-relaxed text-anthracite/80 flex-1">{copy.desc}</p>

        <button
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          data-testid={`formula-toggle-${formula.id}`}
          className="mt-8 inline-flex items-center justify-between gap-4 w-full border-t border-sand/80 pt-5 text-[13px] uppercase tracking-[0.16em] text-anthracite hover:text-bordeaux transition-colors duration-300"
        >
          {f.cta}
          <ChevronDown
            className={`w-4 h-4 transition-transform duration-500 ${open ? "rotate-180" : ""}`}
          />
        </button>

        <AnimatePresence initial={false}>
          {open && (
            <motion.div
              key="panel"
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: "auto", opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
              className="overflow-hidden"
              data-testid={`formula-panel-${formula.id}`}
            >
              {formula.menu.length > 0 ? (
                <ul className="pt-6 space-y-5">
                  {formula.menu.map((item, i) => (
                    <li key={i}>
                      <div className="flex items-baseline">
                        <span className="text-base">{item.name[lang]}</span>
                        <span className="menu-leader" aria-hidden="true" />
                        <span className="font-serif text-base">{item.price ? `${item.price} €` : ""}</span>
                      </div>
                      {item.description?.[lang] && (
                        <p className="mt-1 text-sm font-light italic text-anthracite/55">{item.description[lang]}</p>
                      )}
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="pt-6 font-serif italic text-base text-anthracite/65">{f.placeholder}</p>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </article>
  );
};

const FormuleSection = ({ compact = false }) => {
  const { t } = useLang();
  const { formule } = useContent();
  const f = t.formule;

  return (
    <section
      id="formule"
      className={compact ? "mt-16 md:mt-20" : "py-24 md:py-36 bg-sand/30"}
      data-testid="formule-section"
    >
      <div className={compact ? "" : "max-w-7xl mx-auto px-5 md:px-10"}>
        <Reveal>
          <p className="eyebrow text-olive mb-7">{f.label}</p>
          <h2 className={`font-serif tracking-tight leading-[0.98] ${compact ? "text-3xl md:text-5xl" : "text-4xl md:text-6xl lg:text-7xl max-w-3xl"}`}>
            {f.title}
          </h2>
        </Reveal>
        <div className={`mt-12 md:mt-16 grid gap-6 md:gap-8 ${compact ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2 lg:grid-cols-3"}`}>
          {formule.map((formula, i) => (
            <Reveal key={formula.id} delay={0.08 * i}>
              <FormulaCard formula={formula} index={i} />
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};

export default FormuleSection;
